import * as React from "react";
import { Search, X, Loader2 } from "lucide-react";

import { cn } from "../../lib/utils";
import { Input } from "./input";
import { Button } from "./button";

export interface SearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange"
> {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  loading?: boolean;
  debounceMs?: number;
  showClearButton?: boolean;
  showSearchButton?: boolean;
  /** 28px (`sm`) or 32px (`default`) control height. */
  inputSize?: "sm" | "default";
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      value = "",
      onChange,
      onSearch,
      onClear,
      loading = false,
      debounceMs = 300,
      showClearButton = true,
      showSearchButton = false,
      inputSize = "default",
      disabled,
      placeholder = "Search...",
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState(value);
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    React.useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange?.(newValue);

      if (debounceMs > 0 && onSearch) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          onSearch(newValue);
        }, debounceMs);
      }
    };

    const handleClear = () => {
      setInternalValue("");
      onChange?.("");
      onClear?.();
      onSearch?.("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && onSearch) {
        e.preventDefault();
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        onSearch(internalValue);
      }
      if (e.key === "Escape" && internalValue) {
        handleClear();
      }
      props.onKeyDown?.(e);
    };

    const handleSearchClick = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      onSearch?.(internalValue);
    };

    React.useEffect(() => {
      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, []);

    return (
      <div className="flex items-center gap-1">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-tertiary"
            aria-hidden
          />
          <Input
            ref={ref}
            type="search"
            inputSize={inputSize}
            value={internalValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || loading}
            className={cn(
              "pl-7 [&::-webkit-search-cancel-button]:appearance-none",
              (showClearButton && internalValue) || loading ? "pr-7" : "",
              className,
            )}
            {...props}
          />
          {loading ? (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <Loader2
                className="size-3.5 animate-spin text-text-tertiary"
                aria-hidden
              />
            </div>
          ) : (
            showClearButton &&
            internalValue && (
              <button
                type="button"
                onClick={handleClear}
                className={cn(
                  "absolute right-1 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-sm",
                  "text-text-tertiary",
                  "transition-[background-color,color] duration-100 ease-out-fast",
                  "hover:bg-hover hover:text-text",
                  "focus-visible:outline-none focus-fill",
                  "disabled:pointer-events-none disabled:opacity-50",
                )}
                disabled={disabled}
              >
                <X className="size-3.5" aria-hidden />
                <span className="sr-only">Clear search</span>
              </button>
            )
          )}
        </div>
        {showSearchButton && (
          <Button
            type="button"
            variant="outline"
            size={inputSize === "sm" ? "sm" : "default"}
            onClick={handleSearchClick}
            disabled={disabled || loading}
          >
            {loading ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <Search aria-hidden />
            )}
            Search
          </Button>
        )}
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
